import StatusChip from 'components/@extended/StatusChip';
import ConfirmationModal from 'components/modals/confirmation/ConfirmationModal';
import { ModalFooter } from 'components/modals/ModalFooter';
import { ModalHeader } from 'components/modals/ModalHeader';
import { useModal } from 'components/modals/useModal';
import { Button } from 'components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from 'components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from 'components/ui/tabs';
import { format, parseISO } from 'date-fns';
import { useEffectiveDateFormat } from 'hooks/useEffectiveDateFormat';
import {
    employeePriceRange,
    inForcePeriodOf,
    pricedEmployeeRows,
    sortPeriods,
} from 'pages/pricelists/modal/employeePricing';
import { useToday } from 'pages/pricelists/modal/useEmployeePricing';
import { formatRateRange } from 'pages/pricelists/rateRange';
import { useState } from 'react';
import { useUpdatePricelistMutation } from 'store/api/pricelists';
import { Pricelist, PricelistPeriod, PricingKind } from 'types/pricelists';
import { t } from 'utils/getTranslations';
import { FollowedPricelist, ModelPricelistOptions } from './ModelPricelistOptions';

interface Props {
    contractId: number;
    pricelist: Pricelist;
    canEdit: boolean;
    onEdit: () => void;
    onChanged: () => void;
}

/**
 * A contract's employee-priced list: the model it follows, read-only, or its own periods, each with its people and
 * prices. The actions switch between the two.
 */
export function ContractEmployeePricelist({ contractId, pricelist, canEdit, onEdit, onChanged }: Props): JSX.Element {
    const today = useToday();
    const dateFormat = useEffectiveDateFormat();
    const { openModal, closeModal } = useModal();
    const [updatePricelist, { isLoading: isSaving }] = useUpdatePricelistMutation();
    const followed = pricelist.followsPricelist ?? null;
    const periods = sortPeriods(pricelist.periods ?? []);
    const inForce = inForcePeriodOf(periods, today);
    const range = employeePriceRange(inForce?.rates ?? []);
    const formatDay = (day: string) => format(parseISO(day), dateFormat);

    const saveLink = async (followsPricelist: { id: number } | null) => {
        try {
            await updatePricelist({
                entityId: String(pricelist.id),
                name: pricelist.name,
                scopeTypeCode: 'CONTRACT',
                contractId,
                currencyTypeCode: pricelist.currencyTypeCode ?? null,
                validFrom: null,
                validTo: null,
                pricingKind: PricingKind.EMPLOYEE,
                rates: [],
                followsPricelist,
            }).unwrap();
            onChanged();
        } catch {
            // The API layer has already said the save failed; the list stays as it was.
        }
    };

    const confirmFollow = (model: FollowedPricelist) => {
        openModal(
            <ConfirmationModal
                titleId="contracts.pricing.follow-confirm-title"
                titleValues={{ name: model.name }}
                confirmId="contracts.pricing.follow-confirm-action"
                content={t('contracts.pricing.follow-confirm-body')}
                onCancel={closeModal}
                onConfirm={() => {
                    closeModal();
                    closeModal();
                    void saveLink({ id: model.id });
                }}
            />,
            closeModal
        );
    };

    const openFollowPicker = () => {
        openModal(
            <FollowModelPricelistModal
                currencyTypeCode={pricelist.currencyTypeCode}
                followedId={followed?.id ?? null}
                onClose={closeModal}
                onPick={confirmFollow}
            />,
            closeModal,
            { width: 480 }
        );
    };

    const periodLabel = (period: PricelistPeriod, index: number) => {
        if (period.validFrom) {
            return t('pricelists.employee.period-from', { date: formatDay(period.validFrom) });
        }
        const next = periods[index + 1]?.validFrom;
        return next
            ? t('pricelists.employee.period-before', { date: formatDay(next) })
            : t('pricelists.employee.period-all-dates');
    };

    return (
        <div className="flex flex-col gap-3">
            <div className="flex flex-wrap items-center gap-2">
                <span className="font-medium">
                    {followed
                        ? t('contracts.pricing.follows', { name: followed.name })
                        : t('contracts.pricing.own-price-list')}
                </span>
                <StatusChip
                    type="neutral"
                    label={followed ? t('contracts.pricing.model-price-list') : t('pricelists.employee.per-employee')}
                />
            </div>
            <div className="text-muted-foreground flex flex-wrap gap-x-3 text-sm">
                {range ? (
                    <span className="tabular-nums">
                        {formatRateRange(range.min, range.max, null, pricelist.currencyTypeCode)}
                    </span>
                ) : null}
                <span>
                    {inForce?.validFrom
                        ? t('contracts.pricing.period-in-force-from', { date: formatDay(inForce.validFrom) })
                        : t('contracts.pricing.first-period-in-force')}
                </span>
            </div>
            {followed || periods.length < 2 ? (
                <PeoplePrices period={inForce} currencyTypeCode={pricelist.currencyTypeCode} />
            ) : (
                <Tabs defaultValue={inForce?.id ?? periods[0].id}>
                    <TabsList aria-label={t('contracts.pricing.periods')} className="max-w-full overflow-x-auto">
                        {periods.map((period, index) => (
                            <TabsTrigger key={period.id} value={period.id}>
                                {periodLabel(period, index)}
                            </TabsTrigger>
                        ))}
                    </TabsList>
                    {periods.map((period) => (
                        <TabsContent key={period.id} value={period.id}>
                            <PeoplePrices period={period} currencyTypeCode={pricelist.currencyTypeCode} />
                        </TabsContent>
                    ))}
                </Tabs>
            )}
            {followed ? <p className="text-muted-foreground text-sm">{t('contracts.pricing.follow-hint')}</p> : null}
            {canEdit ? (
                <div className="flex flex-wrap gap-2">
                    {followed ? (
                        <Button variant="outline" size="sm" disabled={isSaving} onClick={() => void saveLink(null)}>
                            {t('contracts.pricing.make-own-copy')}
                        </Button>
                    ) : (
                        <Button variant="outline" size="sm" disabled={isSaving} onClick={onEdit}>
                            {t('edit')}
                        </Button>
                    )}
                    <Button variant="ghost" size="sm" disabled={isSaving} onClick={openFollowPicker}>
                        {followed ? t('contracts.pricing.follow-another') : t('contracts.pricing.follow-a-list')}
                    </Button>
                </div>
            ) : null}
        </div>
    );
}

function PeoplePrices({
    period,
    currencyTypeCode,
}: {
    period: PricelistPeriod | undefined;
    currencyTypeCode?: string | null;
}): JSX.Element {
    const rows = pricedEmployeeRows(period?.rates);
    if (!rows.length) {
        return <p className="text-muted-foreground text-sm">{t('pricelists.employee.no-prices')}</p>;
    }
    return (
        <Table aria-label={t('contracts.pricing.people')}>
            <TableHeader>
                <TableRow>
                    <TableHead>{t('contracts.pricing.person')}</TableHead>
                    <TableHead>{t('contracts.pricing.job-role')}</TableHead>
                    <TableHead className="text-end">{t('contracts.pricing.hourly-price')}</TableHead>
                </TableRow>
            </TableHeader>
            <TableBody>
                {rows.map((rate) => (
                    <TableRow key={rate.employee.id}>
                        <TableCell className="whitespace-normal">{rate.employee.name}</TableCell>
                        <TableCell className="whitespace-normal">
                            {rate.jobRoleName ?? t('pricelists.employee.no-role')}
                        </TableCell>
                        <TableCell className="text-end tabular-nums">
                            {currencyTypeCode ? `${currencyTypeCode} ${rate.hourlyPrice}` : rate.hourlyPrice}
                        </TableCell>
                    </TableRow>
                ))}
            </TableBody>
        </Table>
    );
}

function FollowModelPricelistModal({
    currencyTypeCode,
    followedId,
    onClose,
    onPick,
}: {
    currencyTypeCode?: string | null;
    followedId: number | null;
    onClose: () => void;
    onPick: (model: FollowedPricelist) => void;
}): JSX.Element {
    const [picked, setPicked] = useState<FollowedPricelist | null>(null);
    return (
        <>
            <ModalHeader title={t('contracts.pricing.choose-model')} onCancel={onClose} />
            <div className="overflow-y-auto p-6 max-sm:px-4">
                <ModelPricelistOptions
                    currencyTypeCode={currencyTypeCode}
                    selectedId={picked?.id ?? followedId}
                    onPick={setPicked}
                />
            </div>
            <ModalFooter
                onCancel={onClose}
                onSave={() => {
                    if (picked) {
                        onPick(picked);
                    }
                }}
                disableSave={!picked || picked.id === followedId}
                saveButtonTextId="contracts.pricing.follow-confirm-action"
            />
        </>
    );
}
